import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormEvent, useState, useEffect } from "react";
import { useI18n } from "../../i18n/i18n";
import CurrencySelector from "../CurrencySelector";
import { salaryDataService } from "../../lib/salaryDataService";
import { generateSalaryComparisonPDF } from "../../lib/salaryComparisonPdfExport";
import { generateSimpleSalaryPDF } from "../../lib/simpleSalaryPdfExport";
import { 
  Search, 
  TrendingUp, 
  MapPin, 
  Building, 
  Users, 
  Plus, 
  X, 
  Download,
  Share2,
  Save,
  ExternalLink,
  Calendar,
  Info,
  Calculator,
  ArrowRight
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { LanguageLink } from "../LanguageLink";

interface SalaryData {
  id: string;
  jobTitle: string;
  country: string;
  city: string;
  sector: string;
  subsector: string;
  experienceLevel: string;
  yearsOfExperience: number;
  averageSalary: number;
  minSalary: number;
  maxSalary: number;
  medianSalary: number;
  currency: string;
  dataSource: string;
  lastUpdated: string;
  sampleSize?: number;
}

interface SearchCriteria {
  jobTitle: string;
  country: string;
  city: string;
  sector: string;
  subsector: string;
  experienceLevel: string;
  yearsOfExperience: number;
}

interface ComparisonItem extends SalaryData {
  difference?: number;
  percentageDifference?: number;
}

interface SavedComparison {
  id: string;
  timestamp: string;
  title: string;
  searchCriteria: SearchCriteria;
  comparisons: ComparisonItem[];
  currency: string;
}

// Real data lists - synchronized with salaryDataService
const COUNTRIES = [
  "Spain", "United States", "United Kingdom", "Germany", "France", 
  "Italy", "Netherlands", "Switzerland", "Canada", "Australia"
];

const CITIES = {
  "Spain": ["Madrid", "Barcelona", "Valencia", "Sevilla", "Bilbao"],
  "United States": ["New York", "San Francisco", "Los Angeles", "Chicago", "Seattle"],
  "United Kingdom": ["London", "Manchester", "Edinburgh", "Birmingham", "Bristol"],
  "Germany": ["Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne"],
  "France": ["Paris", "Lyon", "Marseille", "Toulouse", "Nice"]
};

const SECTORS = [
  "Technology", "Finance", "Healthcare", "Marketing", "Sales", 
  "Engineering", "Education", "Legal", "Consulting", "Manufacturing"
];

const SUBSECTORS = {
  "Technology": ["Software Development", "Data Science", "Cybersecurity", "DevOps", "Product Management"],
  "Finance": ["Investment Banking", "Corporate Finance", "Risk Management", "Accounting", "Financial Planning"],
  "Healthcare": ["Medicine", "Nursing", "Pharmacy", "Medical Research", "Healthcare Administration"],
  "Marketing": ["Digital Marketing", "Content Marketing", "Brand Management", "Social Media", "SEO/SEM"],
  "Sales": ["Enterprise Sales", "Retail Sales", "Business Development", "Account Management", "Sales Engineering"]
};

// Available job titles - extracted from salaryDataService baseSalaries


// Enhanced salary data generation using the salary service
const generateSalaryData = async (criteria: SearchCriteria): Promise<SalaryData> => {
  try {
    const apiData = await salaryDataService.getSalaryData({
      jobTitle: criteria.jobTitle,
      country: criteria.country,
      city: criteria.city,
      sector: criteria.sector,
      experienceLevel: criteria.experienceLevel
    });

    return {
      id: Date.now().toString(),
      jobTitle: apiData.jobTitle,
      country: apiData.country,
      city: criteria.city,
      sector: apiData.sector,
      subsector: criteria.subsector,
      experienceLevel: apiData.experienceLevel,
      yearsOfExperience: criteria.yearsOfExperience,
      averageSalary: apiData.averageSalary,
      minSalary: apiData.minSalary,
      maxSalary: apiData.maxSalary,
      medianSalary: apiData.medianSalary,
      currency: apiData.currency,
      dataSource: apiData.dataSource,
      lastUpdated: apiData.lastUpdated,
      sampleSize: apiData.sampleSize
    };
  } catch (error) {
    console.error('Error generating salary data:', error);
    // Fallback data
    return {
      id: Date.now().toString(),
      jobTitle: criteria.jobTitle,
      country: criteria.country,
      city: criteria.city,
      sector: criteria.sector,
      subsector: criteria.subsector,
      experienceLevel: criteria.experienceLevel,
      yearsOfExperience: criteria.yearsOfExperience,
      averageSalary: 40000,
      minSalary: 30000,
      maxSalary: 50000,
      medianSalary: 38000,
      currency: "EUR",
      dataSource: "Estimated",
      lastUpdated: new Date().toISOString().split('T')[0]
    };
  }
};

const SalaryComparisonCalculator = () => {
  const { t } = useI18n();
  const [currency, setCurrency] = useState<string>("EUR");
  const [isLoading, setIsLoading] = useState(false);
  
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    jobTitle: "General Position", // Default generic title
    country: "",
    city: "",
    sector: "",
    subsector: "",
    experienceLevel: "",
    yearsOfExperience: 0
  });

  const [searchResults, setSearchResults] = useState<SalaryData | null>(null);
  const [comparisons, setComparisons] = useState<ComparisonItem[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [availableSubsectors, setAvailableSubsectors] = useState<string[]>([]);
  const [savedComparisons, setSavedComparisons] = useState<SavedComparison[]>([]);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Exchange rates for currency conversion
  const [exchangeRates] = useState({
    EUR: 1,
    USD: 1.08,
    GBP: 0.85,
    CHF: 0.95,
  });

  useEffect(() => {
    if (searchCriteria.country && CITIES[searchCriteria.country as keyof typeof CITIES]) {
      setAvailableCities(CITIES[searchCriteria.country as keyof typeof CITIES]);
    } else {
      setAvailableCities([]);
    }
  }, [searchCriteria.country]);

  useEffect(() => {
    if (searchCriteria.sector && SUBSECTORS[searchCriteria.sector as keyof typeof SUBSECTORS]) {
      setAvailableSubsectors(SUBSECTORS[searchCriteria.sector as keyof typeof SUBSECTORS]);
    } else {
      setAvailableSubsectors([]);
    }
  }, [searchCriteria.sector]);

  const convertCurrency = (amount: number, fromCurrency: string, toCurrency: string): number => {
    const fromRate = exchangeRates[fromCurrency as keyof typeof exchangeRates] || 1;
    const toRate = exchangeRates[toCurrency as keyof typeof exchangeRates] || 1;
    return (amount / fromRate) * toRate;
  };

  const formatCurrency = (amount: number, targetCurrency?: string): string => {
    const displayCurrency = targetCurrency || currency;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Load saved comparisons on component mount
  useEffect(() => {
    const saved = localStorage.getItem('salaryComparisons');
    if (saved) {
      try {
        setSavedComparisons(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved comparisons:', error);
      }
    }
  }, []);

  // Save comparison function
  const saveComparison = () => {
    if (comparisons.length === 0) {
      alert(t('tools.salaryComparison.errors.noComparisons'));
      return;
    }

    const comparisonId = `comparison_${Date.now()}`;
    const comparisonData = {
      id: comparisonId,
      timestamp: new Date().toISOString(),
      searchCriteria: { ...searchCriteria },
      comparisons: [...comparisons],
      currency: currency,
      title: `${searchCriteria.jobTitle} - ${searchCriteria.country}`,
    };

    const updatedSaved = [...savedComparisons, comparisonData];
    setSavedComparisons(updatedSaved);
    localStorage.setItem('salaryComparisons', JSON.stringify(updatedSaved));
    
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  // Share comparison function
  const shareComparison = async () => {
    if (comparisons.length === 0) {
      alert(t('tools.salaryComparison.errors.noComparisons'));
      return;
    }

    const shareData = {
      jobTitle: searchCriteria.jobTitle,
      country: searchCriteria.country,
      city: searchCriteria.city,
      sector: searchCriteria.sector,
      subsector: searchCriteria.subsector,
      experienceLevel: searchCriteria.experienceLevel,
      yearsOfExperience: searchCriteria.yearsOfExperience,
      currency: currency,
      comparisons: comparisons.map(comp => ({
        id: comp.id,
        jobTitle: comp.jobTitle,
        country: comp.country,
        city: comp.city,
        sector: comp.sector,
        subsector: comp.subsector,
        experienceLevel: comp.experienceLevel,
        yearsOfExperience: comp.yearsOfExperience,
        averageSalary: comp.averageSalary,
        currency: comp.currency,
      }))
    };

    try {
      const encodedData = btoa(JSON.stringify(shareData));
      const shareUrl = `${window.location.origin}${window.location.pathname}?shared=${encodedData}`;
      
      if (navigator.share) {
        await navigator.share({
          title: 'Comparación Salarial - Finanzly',
          text: `Comparación salarial para ${searchCriteria.jobTitle}`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShowShareModal(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback: show share modal with URL
      setShowShareModal(true);
    }
  };

  // Load shared comparison from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedData = urlParams.get('shared');
    
    if (sharedData) {
      try {
        const decoded = JSON.parse(atob(sharedData));
        setSearchCriteria({
          jobTitle: decoded.jobTitle || '',
          country: decoded.country || '',
          city: decoded.city || '',
          sector: decoded.sector || '',
          subsector: decoded.subsector || '',
          experienceLevel: decoded.experienceLevel || '',
          yearsOfExperience: decoded.yearsOfExperience || 0,
        });
        setCurrency(decoded.currency || 'EUR');
        
        if (decoded.comparisons && decoded.comparisons.length > 0) {
          setComparisons(decoded.comparisons.map((comp: any, index: number) => ({
            ...comp,
            id: `shared_${Date.now()}_${index}`,
          })));
        }
      } catch (error) {
        console.error('Error loading shared comparison:', error);
      }
    }
  }, []);

  // Delete saved comparison
  const deleteSavedComparison = (id: string) => {
    const updated = savedComparisons.filter(comp => comp.id !== id);
    setSavedComparisons(updated);
    localStorage.setItem('salaryComparisons', JSON.stringify(updated));
  };

  // Load saved comparison
  const loadSavedComparison = (savedComparison: SavedComparison) => {
    setSearchCriteria(savedComparison.searchCriteria);
    setComparisons(savedComparison.comparisons);
    setCurrency(savedComparison.currency);
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (!searchResults) {
      alert(t('tools.salaryComparison.errors.noComparisons') || 'No data to export');
      return;
    }

    try {
      console.log('Attempting to generate PDF...');
      const success = generateSimpleSalaryPDF(
        searchCriteria,
        searchResults,
        comparisons,
        currency,
        t
      );

      if (success) {
        // Show success message
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);
      } else {
        alert('Error generating PDF. Please try again.');
      }
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert('Error generating PDF: ' + (error as Error).message);
    }
  };

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!searchCriteria.country || !searchCriteria.sector) {
      return;
    }

    setIsLoading(true);
    
    // Get real salary data using our service
    const salaryData = await generateSalaryData(searchCriteria);
    setSearchResults(salaryData);
    setIsLoading(false);
  };

  const addComparison = () => {
    if (searchResults) {
      const newComparison: ComparisonItem = { ...searchResults };
      if (comparisons.length > 0) {
        const baseSalary = comparisons[0].averageSalary;
        const convertedBaseSalary = convertCurrency(baseSalary, comparisons[0].currency, newComparison.currency);
        const difference = newComparison.averageSalary - convertedBaseSalary;
        const percentageDifference = (difference / convertedBaseSalary) * 100;
        
        newComparison.difference = difference;
        newComparison.percentageDifference = percentageDifference;
      }
      setComparisons([...comparisons, newComparison]);
    }
  };

  const removeComparison = (id: string) => {
    setComparisons(comparisons.filter(comp => comp.id !== id));
  };

  const clearAll = () => {
    setSearchResults(null);
    setComparisons([]);
    setSearchCriteria({
      jobTitle: "",
      country: "",
      city: "",
      sector: "",
      subsector: "",
      experienceLevel: "",
      yearsOfExperience: 0
    });
  };

  return (
    <div className="container mx-auto px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-6">
          {t("tools.salaryComparison.title")}
        </h2>
        <p className="text-neutral-600 text-center mb-8 max-w-3xl mx-auto leading-relaxed">
          {t("tools.salaryComparison.description")}
        </p>

        <div className="space-y-8">
          {/* Search Form */}
          <Card className="bg-neutral-100 rounded-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-[#EA580C]" />
            {t("tools.salaryComparison.searchSalary")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
              {/* Country */}
              <div className="space-y-2">
                <Label>{t("tools.salaryComparison.country")}</Label>
                <Select 
                  value={searchCriteria.country} 
                  onValueChange={(value) => setSearchCriteria({
                    ...searchCriteria,
                    country: value,
                    city: "" // Reset city when country changes
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("tools.salaryComparison.countryPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label>{t("tools.salaryComparison.city")}</Label>
                <Select 
                  value={searchCriteria.city} 
                  onValueChange={(value) => setSearchCriteria({
                    ...searchCriteria,
                    city: value
                  })}
                  disabled={!searchCriteria.country}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("tools.salaryComparison.cityPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sector */}
              <div className="space-y-2">
                <Label>{t("tools.salaryComparison.sector")}</Label>
                <Select 
                  value={searchCriteria.sector} 
                  onValueChange={(value) => setSearchCriteria({
                    ...searchCriteria,
                    sector: value,
                    subsector: "" // Reset subsector when sector changes
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("tools.salaryComparison.sectorPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTORS.map(sector => (
                      <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subsector */}
              <div className="space-y-2">
                <Label>{t("tools.salaryComparison.subsector")}</Label>
                <Select 
                  value={searchCriteria.subsector} 
                  onValueChange={(value) => setSearchCriteria({
                    ...searchCriteria,
                    subsector: value
                  })}
                  disabled={!searchCriteria.sector}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("tools.salaryComparison.subsectorPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSubsectors.map(subsector => (
                      <SelectItem key={subsector} value={subsector}>{subsector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Experience Level */}
              <div className="space-y-2">
                <Label>{t("tools.salaryComparison.experienceLevel")}</Label>
                <Select 
                  value={searchCriteria.experienceLevel} 
                  onValueChange={(value) => setSearchCriteria({
                    ...searchCriteria,
                    experienceLevel: value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="junior">{t("tools.salaryComparison.junior")}</SelectItem>
                    <SelectItem value="mid">{t("tools.salaryComparison.mid")}</SelectItem>
                    <SelectItem value="senior">{t("tools.salaryComparison.senior")}</SelectItem>
                    <SelectItem value="executive">{t("tools.salaryComparison.executive")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end pt-4 border-t border-neutral-200">
              <div className="w-full sm:flex-1">
                <Label className="text-sm font-medium text-neutral-700 mb-2 block">Currency</Label>
                <CurrencySelector 
                  value={currency} 
                  onChange={setCurrency}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Button 
                  type="submit" 
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium px-6 py-3 w-full sm:w-auto"
                  disabled={isLoading || !searchCriteria.jobTitle || !searchCriteria.country || !searchCriteria.sector}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t("tools.salaryComparison.loading")}
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      {t("tools.salaryComparison.searchSalary")}
                    </>
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={clearAll}
                  className="w-full sm:w-auto px-6 py-3"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

          {/* Loading State */}
          {isLoading && (
            <Card className="bg-white rounded-xl">
              <CardContent className="py-16">
                <div className="text-center max-w-md mx-auto">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#EA580C] mx-auto mb-6"></div>
                  <p className="text-xl font-medium text-neutral-700 mb-2">{t("tools.salaryComparison.loading")}</p>
                  <p className="text-sm text-neutral-500">Gathering real-time salary data...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search Results */}
          {searchResults && !isLoading && (
            <Card className="bg-white rounded-xl">
              <CardHeader className="pb-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <TrendingUp className="h-6 w-6 text-[#EA580C]" />
                    {t("tools.salaryComparison.searchResults")}
                  </CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={addComparison}
                      className="flex items-center gap-2 hover:border-[#EA580C] hover:text-[#EA580C]"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="hidden sm:inline">{t("tools.salaryComparison.addComparison")}</span>
                      <span className="sm:hidden">Add</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex items-center gap-2 hover:border-[#EA580C] hover:text-[#EA580C]"
                      onClick={saveComparison}
                    >
                      <Save className="h-4 w-4" />
                      <span className="hidden sm:inline">{t("tools.salaryComparison.saveComparison")}</span>
                      <span className="sm:hidden">Save</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex items-center gap-2 hover:border-[#EA580C] hover:text-[#EA580C]"
                      onClick={shareComparison}
                    >
                      <Share2 className="h-4 w-4" />
                      <span className="hidden sm:inline">{t("tools.salaryComparison.shareComparison")}</span>
                      <span className="sm:hidden">Share</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
          <CardContent className="space-y-6">
            {/* Salary Information */}
            <div className="bg-[#FFF7ED] p-4 rounded-lg border border-[#FB923C]/20">
              <h3 className="text-xl font-semibold text-neutral-800 mb-2">{t("tools.salaryComparison.salaryInformation")}</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="outline" className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {searchResults.city}, {searchResults.country}
                </Badge>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  {searchResults.sector}
                </Badge>
                {searchResults.subsector && (
                  <Badge variant="outline">
                    {searchResults.subsector}
                  </Badge>
                )}
                <Badge variant="outline" className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {searchResults.experienceLevel}
                </Badge>
              </div>
            </div>

              {/* Salary Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-[#EA580C] to-[#FB923C] text-white rounded-xl shadow-lg">
                  <p className="text-sm opacity-90 font-medium">{t("tools.salaryComparison.averageSalary")}</p>
                  <p className="text-2xl lg:text-3xl font-bold mt-2">
                    {formatCurrency(convertCurrency(searchResults.averageSalary, searchResults.currency, currency))}
                  </p>
                </div>
                <div className="text-center p-6 bg-neutral-50 rounded-xl border border-neutral-200 hover:shadow-md transition-shadow">
                  <p className="text-sm text-neutral-600 font-medium">{t("tools.salaryComparison.medianSalary")}</p>
                  <p className="text-xl lg:text-2xl font-semibold text-neutral-800 mt-2">
                    {formatCurrency(convertCurrency(searchResults.medianSalary, searchResults.currency, currency))}
                  </p>
                </div>
                <div className="text-center p-6 bg-neutral-50 rounded-xl border border-neutral-200 hover:shadow-md transition-shadow">
                  <p className="text-sm text-neutral-600 font-medium">{t("tools.salaryComparison.minSalary")}</p>
                  <p className="text-xl lg:text-2xl font-semibold text-neutral-800 mt-2">
                    {formatCurrency(convertCurrency(searchResults.minSalary, searchResults.currency, currency))}
                  </p>
                </div>
                <div className="text-center p-6 bg-neutral-50 rounded-xl border border-neutral-200 hover:shadow-md transition-shadow">
                  <p className="text-sm text-neutral-600 font-medium">{t("tools.salaryComparison.maxSalary")}</p>
                  <p className="text-xl lg:text-2xl font-semibold text-neutral-800 mt-2">
                    {formatCurrency(convertCurrency(searchResults.maxSalary, searchResults.currency, currency))}
                  </p>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-neutral-50 p-6 rounded-xl border border-neutral-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-neutral-600">
                  <span className="flex items-center gap-2">
                    <Info className="h-4 w-4 flex-shrink-0" />
                    <span><strong>{t("tools.salaryComparison.dataSource")}:</strong> {searchResults.dataSource}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    <span><strong>{t("tools.salaryComparison.lastUpdated")}:</strong> {searchResults.lastUpdated}</span>
                  </span>
                </div>
                {searchResults.sampleSize && (
                  <p className="text-xs text-neutral-500 mt-3 flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Based on {searchResults.sampleSize} salary reports
                  </p>
                )}
              </div>

              {/* Disclaimer */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <p className="text-sm text-blue-800 leading-relaxed">
                  <Info className="h-4 w-4 inline mr-2 flex-shrink-0" />
                  {t("tools.salaryComparison.disclaimer")}
                </p>
              </div>
          </CardContent>
        </Card>
      )}

          {/* Comparisons */}
          {comparisons.length > 0 && (
            <Card className="bg-white rounded-xl">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Users className="h-6 w-6 text-[#EA580C]" />
                  {t("tools.salaryComparison.comparisonResults")} ({comparisons.length})
                </CardTitle>
              </CardHeader>
          <CardContent>
            <div className="space-y-4">
                {comparisons.map((comparison, index) => (
                  <div key={comparison.id} className="border border-neutral-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg lg:text-xl text-neutral-800 mb-3">{comparison.jobTitle}</h4>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs bg-neutral-50">
                            <MapPin className="h-3 w-3 mr-1" />
                            {comparison.city}, {comparison.country}
                          </Badge>
                          <Badge variant="outline" className="text-xs bg-neutral-50">
                            <Building className="h-3 w-3 mr-1" />
                            {comparison.sector}
                          </Badge>
                          <Badge variant="outline" className="text-xs bg-neutral-50">
                            <Users className="h-3 w-3 mr-1" />
                            {comparison.experienceLevel}
                          </Badge>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => removeComparison(comparison.id)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 self-start"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-neutral-50 rounded-lg">
                        <p className="text-sm text-neutral-600 font-medium">{t("tools.salaryComparison.averageSalary")}</p>
                        <p className="text-lg lg:text-xl font-semibold text-neutral-800 mt-1">
                          {formatCurrency(convertCurrency(comparison.averageSalary, comparison.currency, currency))}
                        </p>
                      </div>

                      {comparison.difference !== undefined && (
                        <>
                          <div className="text-center p-4 bg-neutral-50 rounded-lg">
                            <p className="text-sm text-neutral-600 font-medium">{t("tools.salaryComparison.salaryDifference")}</p>
                            <p className={`text-lg lg:text-xl font-semibold mt-1 ${comparison.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {comparison.difference > 0 ? '+' : ''}{formatCurrency(Math.abs(convertCurrency(comparison.difference, comparison.currency, currency)))}
                            </p>
                          </div>
                          
                          <div className="text-center p-4 bg-neutral-50 rounded-lg">
                            <p className="text-sm text-neutral-600 font-medium">{t("tools.salaryComparison.percentageDifference")}</p>
                            <p className={`text-lg lg:text-xl font-semibold mt-1 ${comparison.percentageDifference! > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {comparison.percentageDifference! > 0 ? '+' : ''}{comparison.percentageDifference!.toFixed(1)}%
                            </p>
                          </div>

                          <div className="text-center p-4 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-lg">
                            <p className="text-sm text-neutral-600 font-medium mb-2">Comparison</p>
                            <Badge 
                              variant={comparison.difference > 0 ? "default" : "secondary"}
                              className={`text-xs px-3 py-1 ${comparison.difference > 0 ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}`}
                            >
                              {Math.abs(comparison.percentageDifference!).toFixed(1)}% {comparison.difference > 0 ? t("tools.salaryComparison.higher") : t("tools.salaryComparison.lower")}
                            </Badge>
                          </div>
                        </>
                      )}
                  </div>

                  {index < comparisons.length - 1 && <Separator className="mt-4" />}
                </div>
              ))}
            </div>
            
              <div className="flex justify-center mt-8 pt-6 border-t border-neutral-200">
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2 px-6 py-3 hover:border-[#EA580C] hover:text-[#EA580C] transition-colors"
                  onClick={exportToPDF}
                >
                  <Download className="h-4 w-4" />
                  {t("tools.salaryComparison.exportData")}
                </Button>
              </div>
          </CardContent>
        </Card>
      )}

          {/* Empty State */}
          {!searchResults && !isLoading && (
            <Card className="bg-white rounded-xl">
              <CardContent className="py-16">
                <div className="text-center max-w-4xl mx-auto">
                  <div className="mb-8">
                    <div className="w-20 h-20 bg-[#EA580C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Search className="h-10 w-10 text-[#EA580C]" />
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-neutral-800 mb-4">
                      {t("tools.salaryComparison.searchSalary")}
                    </h3>
                    <p className="text-lg text-neutral-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                      {t("tools.salaryComparison.calculateToSeeResults")}
                    </p>
                  </div>
                  
                  {/* Popular Searches */}
                  <div className="bg-neutral-50 p-6 rounded-xl">
                    <h4 className="text-base font-semibold text-neutral-700 mb-4">
                      {t("tools.salaryComparison.popularSearches")}
                    </h4>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {salaryDataService.getPopularSearches(searchCriteria.country || "Spain").map((search) => (
                        <Button 
                          key={search}
                          variant="outline" 
                          size="sm"
                          onClick={() => setSearchCriteria({...searchCriteria, jobTitle: search})}
                          className="text-sm hover:border-[#EA580C] hover:text-[#EA580C] transition-colors"
                        >
                          {search}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

      {/* Saved Comparisons Section */}
      {savedComparisons.length > 0 && (
        <Card className="bg-white shadow-lg rounded-xl border border-neutral-200">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
              <Save className="h-6 w-6 text-[#EA580C]" />
              {t("tools.salaryComparison.savedComparisons")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {savedComparisons.map((saved) => (
              <div key={saved.id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border">
                <div className="flex-1">
                  <h4 className="font-semibold text-neutral-800">{saved.title}</h4>
                  <p className="text-sm text-neutral-600">
                    {new Date(saved.timestamp).toLocaleDateString()} - {saved.comparisons.length} comparisons
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => loadSavedComparison(saved)}
                    className="hover:border-[#EA580C] hover:text-[#EA580C]"
                  >
                    {t("tools.salaryComparison.loadComparison")}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => deleteSavedComparison(saved.id)}
                    className="hover:border-red-500 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
        </div>

        {/* Educational Content Section */}
        <div className="mt-16 space-y-12">
          {/* How it works */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salaryComparison.howItWorksTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.salaryComparison.howItWorksDesc1")}
              </p>
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.salaryComparison.howItWorksDesc2")}
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6">
                <p className="text-neutral-800 font-medium">
                  {t("tools.salaryComparison.howItWorksExample")}
                </p>
              </div>
              <p className="text-lg text-neutral-700 leading-relaxed">
                {t("tools.salaryComparison.howItWorksDesc3")}
              </p>
            </div>
          </div>

          {/* Ways to use tool */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salaryComparison.waysToUseTitle")}
            </h2>
            <div className="space-y-6">
              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.way1Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.way1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.way2Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.way2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.way3Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.way3Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.way4Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.way4Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salaryComparison.tipsTitle")}
            </h2>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.tip1Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.tip1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.tip2Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.tip2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.tip3Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.tip3Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Factors affecting salary */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salaryComparison.factorsTitle")}
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed mb-8">
              {t("tools.salaryComparison.factorsIntro")}
            </p>

            {/* Location */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">📍</span>
                {t("tools.salaryComparison.locationTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.salaryComparison.locationDesc1")}
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg p-5 mb-4 border-l-4 border-blue-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.salaryComparison.example1Title")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.salaryComparison.locationExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.salaryComparison.locationDesc2")}
              </p>
            </div>

            {/* Experience */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">📚</span>
                {t("tools.salaryComparison.experienceTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.salaryComparison.experienceDesc1")}
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-lg p-5 mb-4 border-l-4 border-purple-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.salaryComparison.example1Title")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.salaryComparison.experienceExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.salaryComparison.experienceDesc2")}
              </p>
            </div>

            {/* Industry */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">🏢</span>
                {t("tools.salaryComparison.industryTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.salaryComparison.industryDesc1")}
              </p>
              <div className="bg-gradient-to-r from-green-50 to-green-100/50 rounded-lg p-5 mb-4 border-l-4 border-green-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.salaryComparison.example1Title")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.salaryComparison.industryExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.salaryComparison.industryDesc2")}
              </p>
            </div>

            {/* Education */}
            <div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">🎓</span>
                {t("tools.salaryComparison.educationTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.salaryComparison.educationDesc1")}
              </p>
              <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-lg p-5 mb-4 border-l-4 border-orange-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.salaryComparison.example1Title")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.salaryComparison.educationExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.salaryComparison.educationDesc2")}
              </p>
            </div>
          </div>

          {/* Real Examples Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salaryComparison.realExamplesTitle")}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Example 1 */}
              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl p-6 border border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4 min-h-[100px]">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl">🗺️</span>
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900 leading-tight">
                    {t("tools.salaryComparison.example1Title")}
                  </h4>
                </div>
                <p className="text-neutral-700 leading-relaxed mb-4 text-sm flex-grow">
                  {t("tools.salaryComparison.example1Desc")}
                </p>
                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-300">
                  <p className="text-xs font-semibold text-blue-700 mb-1">{t("tools.salaryComparison.result")}</p>
                  <p className="text-lg font-bold text-blue-600">{t("tools.salaryComparison.example1Result")}</p>
                </div>
              </div>

              {/* Example 2 */}
              <div className="bg-gradient-to-br from-white to-orange-50/30 rounded-xl p-6 border border-orange-200 hover:border-[#FF6600] transition-all hover:shadow-lg flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4 min-h-[100px]">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FF6600] to-[#FF7700] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl">🔄</span>
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900 leading-tight">
                    {t("tools.salaryComparison.example2Title")}
                  </h4>
                </div>
                <p className="text-neutral-700 leading-relaxed mb-4 text-sm flex-grow">
                  {t("tools.salaryComparison.example2Desc")}
                </p>
                <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-300">
                  <p className="text-xs font-semibold text-orange-700 mb-1">{t("tools.salaryComparison.result")}</p>
                  <p className="text-lg font-bold text-[#FF6600]">{t("tools.salaryComparison.example2Result")}</p>
                </div>
              </div>

              {/* Example 3 */}
              <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl p-6 border border-green-200 hover:border-green-400 transition-all hover:shadow-lg flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4 min-h-[100px]">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl">💼</span>
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900 leading-tight">
                    {t("tools.salaryComparison.example3Title")}
                  </h4>
                </div>
                <p className="text-neutral-700 leading-relaxed mb-4 text-sm flex-grow">
                  {t("tools.salaryComparison.example3Desc")}
                </p>
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-300">
                  <p className="text-xs font-semibold text-green-700 mb-1">{t("tools.salaryComparison.result")}</p>
                  <p className="text-lg font-bold text-green-600">{t("tools.salaryComparison.example3Result")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Common Mistakes Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.salaryComparison.commonMistakesTitle")}
            </h2>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.mistake1Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.mistake1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.mistake2Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.mistake2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.mistake3Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.mistake3Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.salaryComparison.mistake4Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.salaryComparison.mistake4Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Content Section */}
        <div className="mt-16 space-y-8">
          {/* Other Calculators Section */}
          <div className="bg-gradient-to-br from-neutral-50 to-white rounded-xl p-8 border border-neutral-200">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.salaryComparison.otherCalculators")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <LanguageLink
                to="/tools/net-salary"
                className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-[#FF6600] hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-neutral-900 mb-1 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salaryComparison.salaryCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salaryComparison.salaryCalculatorDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/compound-interest"
                className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-[#FF6600] hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-neutral-900 mb-1 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salaryComparison.compoundInterestCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salaryComparison.compoundInterestDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/monthly-savings-calculator"
                className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-[#FF6600] hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-neutral-900 mb-1 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salaryComparison.monthlySavingsCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salaryComparison.monthlySavingsDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/mortgage-calculator"
                className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-[#FF6600] hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-neutral-900 mb-1 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.salaryComparison.mortgageCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.salaryComparison.mortgageDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border border-blue-200">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.salaryComparison.relatedArticles")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* First Article - Takes 2 columns */}
              <LanguageLink
                to="/resources/salary-negotiation-article"
                className="md:col-span-2 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=800&auto=format&fit=crop" 
                  alt="Professional meeting discussing salary negotiation and career advancement"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                      {t("tools.salaryComparison.article1Title")}
                    </h3>
                    <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                      {t("tools.salaryComparison.article1Desc")}
                    </p>
                    <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                      {t("tools.salaryComparison.readMore")}
                      <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </LanguageLink>

              {/* Second Article */}
              <LanguageLink
                to="/resources/first-salary-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=500&auto=format&fit=crop" 
                  alt="Young professional celebrating first salary success with laptop and financial documents"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.salaryComparison.article2Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.salaryComparison.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Third Article */}
              <LanguageLink
                to="/resources/salary-negotiation-worth-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=500&auto=format&fit=crop" 
                  alt="Professional negotiating salary with confidence and financial documents showing career growth"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.salaryComparison.article3Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.salaryComparison.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Fourth Article - Spans remaining space */}
              <LanguageLink
                to="/resources/before-quitting-job-article"
                className="md:col-span-2 lg:col-span-4 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[200px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=800&auto=format&fit=crop" 
                  alt="Professional preparing to quit job with financial planning and career transition concept"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30 group-hover:from-black/80 group-hover:via-black/50 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-center items-start max-w-2xl">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                    {t("tools.salaryComparison.article4Title")}
                  </h3>
                  <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                    {t("tools.salaryComparison.article4Desc")}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.salaryComparison.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tools.salaryComparison.shareComparison")}</DialogTitle>
            <DialogDescription>
              Copy this link to share your salary comparison with others.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-4 bg-neutral-50 rounded-lg">
            <Input 
              value={`${window.location.origin}${window.location.pathname}?shared=${btoa(JSON.stringify({
                jobTitle: searchCriteria.jobTitle,
                country: searchCriteria.country,
                city: searchCriteria.city,
                sector: searchCriteria.sector,
                comparisons: comparisons
              }))}`}
              readOnly
              className="flex-1"
            />
            <Button 
              onClick={() => {
                const shareUrl = `${window.location.origin}${window.location.pathname}?shared=${btoa(JSON.stringify({
                  jobTitle: searchCriteria.jobTitle,
                  country: searchCriteria.country,
                  city: searchCriteria.city,
                  sector: searchCriteria.sector,
                  comparisons: comparisons
                }))}`;
                navigator.clipboard.writeText(shareUrl);
                setShowShareModal(false);
              }}
              size="sm"
            >
              Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Success Toast */}
      {showSaveSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          ✅ {t("tools.salaryComparison.saveSuccess")}
        </div>
      )}
    </div>
  );
};

export default SalaryComparisonCalculator;
